import { useMemo, useState } from 'react'
import { Check, Eraser, Shuffle, Sparkles } from 'lucide-react'
import { ConfirmDialog } from '@components/ConfirmDialog.tsx'
import { Header } from '@components/Header.tsx'
import { useFavicon } from '@hooks/useFavicon.ts'
import { useOnline } from '@hooks/useOnline.ts'
import { useResource } from '@hooks/useResource.ts'
import { api } from '@api'
import type {
	MealDate,
	MealPlanDay,
	MealPlanPutBody,
	RecipeId,
} from '@hewey/shared'
import { addDays, toIsoDate } from './dates.ts'
import { DayColumn, Strip, WeekNavStrip } from './DayColumn.tsx'
import { DayRow } from './DayRow.tsx'
import { DaySelectRow } from './DaySelectRow.tsx'
import { buildAppearances } from './history.ts'
import { type Proposal, suggest, type Suggestion } from './suggest.ts'
import { useMealHistory } from './useMealHistory.ts'
import { useWeekWindow } from './useWeekWindow.ts'
import styles from './Meals.module.css'

const days = (n: number): string => `${n} ${n === 1 ? 'day' : 'days'}`

export const MealsApp = () => {
	useFavicon('meals')

	const { weekStart, dates, phase, sliding, slide, onSlideEnd } =
		useWeekWindow()
	const todayIso = useMemo(() => toIsoDate(new Date()), [])

	const online = useOnline()
	const [selecting, setSelecting] = useState(false)
	const [selected, setSelected] = useState<ReadonlySet<MealDate>>(new Set())
	const [confirmClear, setConfirmClear] = useState(false)
	const [busy, setBusy] = useState(false)

	const [proposal, setProposal] = useState<Proposal | null>(null)
	// Grows as a row is tapped, so each tap moves to the next candidate down
	// rather than back to the one just rejected. Cleared by Shuffle.
	const [rejected, setRejected] = useState<
		ReadonlyMap<MealDate, ReadonlySet<string>>
	>(new Map())

	// Fetch a 3-week window centred on weekStart so prev/next slides have
	// data ready. Range is inclusive on both ends.
	const fetchFrom = useMemo(
		() => toIsoDate(addDays(weekStart, -7)),
		[weekStart],
	)
	const fetchTo = useMemo(
		() => toIsoDate(addDays(weekStart, 14)),
		[weekStart],
	)

	// Poll so a meal planned on another family member's phone shows up here.
	// Paused while selecting: a remote change mid-selection would move rows
	// out from under the days already ticked.
	const resource = useResource(
		['meal-plan', fetchFrom, fetchTo],
		() => api.mealPlan.list(fetchFrom, fetchTo),
		{ pollIntervalMs: selecting ? undefined : 10_000 },
	)

	// Recipes are family-scoped and small (dozens), so a single fetch on
	// mount is plenty for the autocomplete. No polling — picking from a
	// slightly stale list is fine; the worst case is the user has to type
	// the name themselves.
	const recipes = useResource(['recipes'], () => api.recipe.list())

	const history = useMealHistory()

	const recipeList = recipes.data?.recipes ?? []

	const dayMap = useMemo(() => {
		const m = new Map<string, MealPlanDay>()
		for (const d of resource.data?.days ?? []) m.set(d.date, d)
		return m
	}, [resource.data])

	// Older days first so the three-week window, which is the fresher read,
	// wins wherever the two overlap.
	const planDays = useMemo(() => {
		const m = new Map<MealDate, MealPlanDay>()
		for (const d of history.days) m.set(d.date, d)
		for (const d of resource.data?.days ?? []) m.set(d.date, d)
		return [...m.values()]
	}, [history.days, resource.data])

	const appearances = useMemo(() => buildAppearances(planDays), [planDays])

	const proposedByDate = useMemo(() => {
		const m = new Map<MealDate, Suggestion>()
		for (const s of proposal?.suggestions ?? []) m.set(s.date, s)
		return m
	}, [proposal])

	// The days in view that can still be planned. Week navigation is locked
	// while selecting, so this is always the 8 rows on screen.
	const selectable = useMemo(
		() =>
			Array.from({ length: 8 }, (_, i) =>
				toIsoDate(addDays(weekStart, i)),
			).filter((iso) => iso >= todayIso),
		[weekStart, todayIso],
	)

	// The book icon only appears for a recipe that still exists — a deleted
	// one leaves the meal's text behind but nothing to open.
	const hasRecipe = (id: RecipeId | null | undefined): boolean =>
		id != null && recipeList.some((r) => r.id === id)

	const enterSelect = () => {
		// Commit a half-typed meal before its input is replaced by the row's
		// saved text.
		if (document.activeElement instanceof HTMLElement) {
			document.activeElement.blur()
		}
		// Starts now so the older days have arrived by the time days are
		// picked and Suggest is pressed.
		history.load()
		setSelecting(true)
	}

	const dropProposal = () => {
		setProposal(null)
		setRejected(new Map())
	}

	const exitSelect = () => {
		setSelecting(false)
		setSelected(new Set())
		dropProposal()
	}

	const toggleDay = (iso: MealDate) =>
		setSelected((prev) => {
			const next = new Set(prev)
			if (!next.delete(iso)) next.add(iso)
			return next
		})

	const runSuggest = () => {
		setRejected(new Map())
		setProposal(
			suggest({
				fill: [...selected],
				plan: planDays,
				recipes: recipeList,
				appearances,
			}),
		)
	}

	// Re-picks a single day, holding the rest of the proposal in place.
	const swapDay = (date: MealDate) => {
		if (!proposal) return
		const current = proposedByDate.get(date)
		const turned = new Map(rejected)
		if (current) {
			turned.set(
				date,
				new Set([...(turned.get(date) ?? []), current.key]),
			)
		}
		const next = suggest({
			fill: [date],
			pinned: proposal.suggestions.filter((s) => s.date !== date),
			plan: planDays,
			recipes: recipeList,
			appearances,
			rejected: turned,
		})
		// Nothing else to offer for that day — leave what's there.
		if (next.suggestions.length < proposal.suggestions.length) return
		setRejected(turned)
		setProposal(next)
	}

	const onSave = async (
		date: MealDate,
		title: string | null,
		note: string | null,
		recipeId: RecipeId | null,
	) => {
		try {
			const res = await api.mealPlan.put(date, { title, note, recipeId })
			resource.set((curr) => {
				if (!curr) return curr
				const others = curr.days.filter((d) => d.date !== date)
				return res.day
					? { days: [...others, res.day] }
					: { days: others }
			})
		} catch {
			void resource.refetch()
		}
	}

	// Writes several days at once, then reconciles from whatever the server
	// sent back — the same shape as a single-row save.
	const applyWrites = async (
		writes: ReadonlyArray<{ date: MealDate; body: MealPlanPutBody }>,
	) => {
		const results = await Promise.allSettled(
			writes.map((w) => api.mealPlan.put(w.date, w.body)),
		)
		const written = new Map<MealDate, MealPlanDay | null>()
		writes.forEach((w, i) => {
			const r = results[i]
			if (r?.status === 'fulfilled') written.set(w.date, r.value.day)
		})
		resource.set((curr) => {
			if (!curr) return curr
			const byDate = new Map(curr.days.map((d) => [d.date, d]))
			for (const [date, day] of written) {
				if (day) byDate.set(date, day)
				else byDate.delete(date)
			}
			return { days: [...byDate.values()] }
		})
		if (results.some((r) => r.status === 'rejected')) {
			void resource.refetch()
		}
	}

	// Empties every selected day. The server deletes a row whose title and
	// note are both null, so this removes them rather than storing blanks.
	const clearSelected = async () => {
		setBusy(true)
		await applyWrites(
			[...selected].map((date) => ({
				date,
				body: { title: null, note: null, recipeId: null },
			})),
		)
		setBusy(false)
		setConfirmClear(false)
		exitSelect()
	}

	// Writes the proposal to the plan. The note on each day is kept — it's
	// human context, and only the meal was suggested.
	const acceptProposal = async () => {
		if (!proposal) return
		setBusy(true)
		await applyWrites(
			proposal.suggestions.map((s) => ({
				date: s.date,
				body: {
					title: s.title,
					note: dayMap.get(s.date)?.note ?? null,
					recipeId: s.recipeId,
				},
			})),
		)
		setBusy(false)
		exitSelect()
	}

	const count = selected.size
	const filled = proposal?.suggestions.length ?? 0

	return (
		<main className={styles.screen}>
			<Header
				right={
					<button
						type="button"
						className={styles.mode}
						onClick={selecting ? exitSelect : enterSelect}
						// Cancel stays live if the connection drops mid-way.
						disabled={!selecting && !online}
					>
						{selecting ? 'Cancel' : 'Select'}
					</button>
				}
			/>

			<DayColumn
				phase={phase}
				onSlideEnd={onSlideEnd}
				top={
					selecting ? (
						<Strip edge="top">
							<span className={styles.count}>
								{proposal
									? 'Tap a day to change it'
									: count === 0
										? 'Choose days to plan'
										: `${days(count)} selected`}
							</span>
							{proposal ? null : (
								<span className={styles.stripActions}>
									<button
										type="button"
										className={styles.stripAction}
										onClick={() =>
											setSelected(new Set(selectable))
										}
										disabled={count === selectable.length}
									>
										Select all
									</button>
									<button
										type="button"
										className={styles.stripAction}
										onClick={() => setSelected(new Set())}
										disabled={count === 0}
									>
										Deselect all
									</button>
								</span>
							)}
						</Strip>
					) : (
						<WeekNavStrip
							dir="prev"
							disabled={sliding}
							onClick={() => slide('prev')}
						/>
					)
				}
				bottom={
					selecting ? (
						<Strip edge="bottom">
							<span className={styles.actions}>
								{proposal ? (
									<>
										<span className={styles.actionsLeft}>
											<button
												type="button"
												className={styles.action}
												onClick={runSuggest}
												disabled={busy}
											>
												<Shuffle
													size={16}
													aria-hidden
												/>
												Shuffle
											</button>
										</span>
										<button
											type="button"
											className={styles.action}
											onClick={dropProposal}
											disabled={busy}
										>
											Cancel
										</button>
										<span className={styles.actionsRight}>
											<button
												type="button"
												className={`${styles.action} ${styles.actionAccept}`}
												onClick={() =>
													void acceptProposal()
												}
												disabled={
													busy ||
													filled === 0 ||
													!online
												}
											>
												<Check size={16} aria-hidden />
												Accept
											</button>
										</span>
									</>
								) : (
									<>
										<span className={styles.actionsLeft}>
											<button
												type="button"
												className={`${styles.action} ${styles.actionDanger}`}
												onClick={() =>
													setConfirmClear(true)
												}
												disabled={
													count === 0 || !online
												}
											>
												<Eraser size={16} aria-hidden />
												Clear
											</button>
										</span>
										<span />
										<span className={styles.actionsRight}>
											<button
												type="button"
												className={`${styles.action} ${styles.actionSuggest}`}
												onClick={runSuggest}
												disabled={count === 0}
											>
												<Sparkles
													size={16}
													aria-hidden
												/>
												Suggest
											</button>
										</span>
									</>
								)}
							</span>
						</Strip>
					) : (
						<WeekNavStrip
							dir="next"
							disabled={sliding}
							onClick={() => slide('next')}
						/>
					)
				}
			>
				{dates.map((d) => {
					const iso = toIsoDate(d)
					const day = dayMap.get(iso) ?? null
					const editable = iso >= todayIso
					if (!selecting) {
						return (
							<DayRow
								key={iso}
								date={d}
								day={day}
								editable={editable}
								isToday={iso === todayIso}
								recipes={recipeList}
								onSave={(title, note, recipeId) =>
									void onSave(iso, title, note, recipeId)
								}
							/>
						)
					}
					const proposed = proposedByDate.get(iso) ?? null
					return (
						<DaySelectRow
							key={iso}
							date={d}
							day={day}
							isToday={iso === todayIso}
							past={!editable}
							hasRecipe={hasRecipe(
								proposed ? proposed.recipeId : day?.recipeId,
							)}
							selected={selected.has(iso)}
							// While a proposal is up, only the days it covers
							// respond — changing the selection would leave it
							// half-applied.
							pressable={
								editable && (!proposal || selected.has(iso))
							}
							proposedTitle={proposed?.title ?? null}
							onPress={() =>
								proposal ? swapDay(iso) : toggleDay(iso)
							}
						/>
					)
				})}
			</DayColumn>

			{resource.error ? (
				<p className={styles.status}>
					Couldn't load meal plan.{' '}
					<button
						type="button"
						className={styles.retry}
						onClick={() => void resource.refetch()}
					>
						Retry
					</button>
				</p>
			) : proposal && filled < count ? (
				<p className={styles.status}>
					Filled {filled} of {days(count)}. Add more recipes to fill a
					whole week.
				</p>
			) : null}

			<ConfirmDialog
				open={confirmClear}
				title={`Clear ${days(count)}?`}
				body="This will remove the meal and the note from each selected day."
				confirmLabel="Clear"
				destructive
				busy={busy}
				onConfirm={() => void clearSelected()}
				onClose={() => setConfirmClear(false)}
			/>
		</main>
	)
}
